'use client';

import React, { useState } from 'react';
import { toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { Device } from './DeviceInventoryContent';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

const statusConfig: Record<string, { label: string; color: string }> = {
  online: { label: 'Online', color: 'text-green-400' },
  offline: { label: 'Offline', color: 'text-red-400' },
  latenza_alta: { label: 'Latenza Alta', color: 'text-amber-400' },
  intermittente: { label: 'Intermittente', color: 'text-orange-400' },
  manutenzione: { label: 'Manutenzione', color: 'text-purple-400' },
};

const categoriaIcons: Record<string, string> = {
  laboratorio: 'BeakerIcon',
  ufficio: 'ComputerDesktopIcon',
  medico: 'HeartIcon',
  iot: 'CpuChipIcon',
  server: 'ServerIcon',
  virtuale: 'CloudIcon',
  rete: 'GlobeAltIcon',
};

interface DeviceDetailPanelProps {
  device: Device;
  onClose: () => void;
  onRefresh: () => void;
}

export default function DeviceDetailPanel({ device, onClose, onRefresh }: DeviceDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'rete' | 'note'>('overview');
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({ note: device.note, responsabile: device.responsabile, reparto: device.reparto });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const supabase = createClient();
  const { canWrite } = useAuth();

  const sc = statusConfig[device.stato] || statusConfig.offline;

  const handleSave = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('devices')
        .update({ note: editData.note, responsabile: editData.responsabile, reparto: editData.reparto })
        .eq('id', device.id);
      if (error) throw error;
      toast.success('Dispositivo aggiornato');
      setEditing(false);
      onRefresh();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleteConfirm(false);
    try {
      const { error } = await supabase.from('devices').delete().eq('id', device.id);
      if (error) throw error;
      toast.success('Dispositivo eliminato');
      onClose();
      onRefresh();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const tabs = [
    { key: 'overview', label: 'Panoramica' },
    { key: 'rete', label: 'Rete' },
    { key: 'note', label: 'Note' },
  ] as const;

  return (
    <div className="bg-surface border border-border rounded-xl overflow-hidden flex flex-col h-full max-h-[calc(100vh-180px)] sticky top-0">
      <ConfirmDialog
        open={deleteConfirm}
        title="Elimina dispositivo"
        message={`Sei sicuro di voler eliminare "${device.hostname}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(false)}
        danger
      />

      {/* Header */}
      <div className="flex items-start justify-between p-4 border-b border-border">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-surface-elevated border border-border flex items-center justify-center flex-shrink-0">
            <Icon name={categoriaIcons[device.categoria] as any || 'ServerIcon'} size={18} className="text-sky-400" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white font-mono truncate">{device.hostname}</p>
            <p className="text-xs text-muted-color capitalize">{device.categoria}</p>
          </div>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors flex-shrink-0">
          <Icon name="XMarkIcon" size={15} className="text-muted-color" />
        </button>
      </div>

      {/* Status row */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-surface-elevated/30">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-bold ${sc.color}`}>{sc.label}</span>
          {device.ping_ms !== null && (
            <span className="text-xs text-muted-color font-mono">{device.ping_ms}ms</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {canWrite() && (
            <>
              <button
                onClick={() => setEditing(v => !v)}
                className="flex items-center gap-1.5 text-sm text-secondary-color hover:text-white bg-surface-elevated border border-border rounded-lg px-3 py-2 transition-all font-semibold"
              >
                <Icon name="PencilSquareIcon" size={14} />
                Modifica
              </button>
              <button
                onClick={() => setDeleteConfirm(true)}
                className="flex items-center gap-1.5 text-sm text-red-400 hover:text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 transition-all font-semibold"
              >
                <Icon name="TrashIcon" size={14} />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border">
        {tabs.map((tab) => (
          <button
            key={`detail-tab-${tab.key}`}
            onClick={() => setActiveTab(tab.key)}
            className={`flex-1 py-2.5 text-xs font-medium transition-colors ${activeTab === tab.key ? 'text-sky-400 border-b-2 border-sky-400' : 'text-muted-color hover:text-secondary-color'}`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto scrollbar-thin p-4">
        {activeTab === 'overview' && (
          <dl className="space-y-3">
            {[
              { label: 'Hostname', value: device.hostname, mono: true },
              { label: 'Categoria', value: device.categoria, mono: false },
              { label: 'Modello', value: device.modello || '—', mono: false },
              { label: 'Firmware', value: device.firmware || '—', mono: true },
              { label: 'N. Seriale', value: device.numero_seriale || '—', mono: true },
              { label: 'Reparto', value: editing ? null : (device.reparto || '—'), mono: false },
              { label: 'Responsabile', value: editing ? null : (device.responsabile || '—'), mono: false },
              { label: 'Uptime', value: device.uptime_ore ? `${Math.floor(device.uptime_ore / 24)}g ${device.uptime_ore % 24}h` : '—', mono: true },
              { label: 'Ultima Visita', value: device.ultima_visita ? new Date(device.ultima_visita).toLocaleString('it-IT') : 'Mai', mono: false },
            ].map((row) => (
              <div key={`detail-${row.label}`} className="flex items-start justify-between gap-3">
                <dt className="text-xs text-muted-color flex-shrink-0 w-28">{row.label}</dt>
                {row.value !== null ? (
                  <dd className={`text-xs text-right truncate ${row.mono ? 'font-mono text-white' : 'text-secondary-color'}`}>
                    {row.value}
                  </dd>
                ) : (
                  <dd className="flex-1">
                    {row.label === 'Reparto' ? (
                      <input
                        value={editData.reparto}
                        onChange={e => setEditData(p => ({ ...p, reparto: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-sky-500"
                      />
                    ) : (
                      <input
                        value={editData.responsabile}
                        onChange={e => setEditData(p => ({ ...p, responsabile: e.target.value }))}
                        className="w-full bg-surface-elevated border border-border rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-sky-500"
                      />
                    )}
                  </dd>
                )}
              </div>
            ))}
            {editing && (
              <div className="flex gap-2 pt-2">
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 bg-sky-500 hover:bg-sky-400 text-white text-xs font-bold rounded-lg py-2 transition-all disabled:opacity-60"
                >
                  {saving ? 'Salvataggio...' : 'Salva'}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="flex-1 bg-surface-elevated border border-border text-secondary-color text-xs font-semibold rounded-lg py-2 transition-all hover:text-white"
                >
                  Annulla
                </button>
              </div>
            )}
          </dl>
        )}

        {activeTab === 'rete' && (
          <dl className="space-y-3">
            {[
              { label: 'Indirizzo IP', value: device.ip_address || '—', mono: true },
              { label: 'MAC Address', value: device.mac_address || '—', mono: true },
              { label: 'Tipo IP', value: device.ip_type === 'statico' ? 'Statico' : 'DHCP', mono: false },
              { label: 'Subnet', value: (device.subnet as any)?.cidr || '—', mono: true },
              { label: 'VLAN', value: (device.vlan as any) ? `VLAN ${(device.vlan as any).vlan_id} — ${(device.vlan as any).nome}` : '—', mono: false },
              { label: 'Latenza', value: device.ping_ms !== null ? `${device.ping_ms}ms` : '—', mono: true },
            ].map((row) => (
              <div key={`net-${row.label}`} className="flex items-start justify-between gap-3">
                <dt className="text-xs text-muted-color flex-shrink-0 w-28">{row.label}</dt>
                <dd className={`text-xs text-right truncate ${row.mono ? 'font-mono text-white' : 'text-secondary-color'}`}>
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        )}

        {activeTab === 'note' && (
          <div className="space-y-3">
            {editing ? (
              <>
                <textarea
                  value={editData.note}
                  onChange={e => setEditData(p => ({ ...p, note: e.target.value }))}
                  rows={6}
                  placeholder="Note sul dispositivo..."
                  className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-xs text-white placeholder:text-muted-color focus:outline-none focus:border-sky-500 resize-none"
                />
                <div className="flex gap-2">
                  <button onClick={handleSave} disabled={saving} className="flex-1 bg-sky-500 hover:bg-sky-400 text-white text-xs font-bold rounded-lg py-2 transition-all disabled:opacity-60">
                    {saving ? 'Salvataggio...' : 'Salva Note'}
                  </button>
                  <button onClick={() => setEditing(false)} className="flex-1 bg-surface-elevated border border-border text-secondary-color text-xs font-semibold rounded-lg py-2 hover:text-white">
                    Annulla
                  </button>
                </div>
              </>
            ) : (
              <p className="text-xs text-secondary-color leading-relaxed">
                {device.note || 'Nessuna nota disponibile per questo dispositivo.'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}